import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, useNavigate, generatePath, useLocation } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import { useLayout } from '../hooks/layout';
import Markdown from '../shared/Markdown';
import { useAutocompleteQuery } from '../services/recipes';
import { InputBase, Box, Typography, Backdrop, IconButton } from '@mui/material';
import useAutocomplete from '@mui/material/useAutocomplete';
import { autocompleteClasses } from '@mui/material/Autocomplete';
import logoImg from '../assets/images/logo.png';
import { SearchIcon } from '../shared/Icons';

const SearchContainer = styled('div')(({ theme }) => ({
  color: '#B0A3A3',
  position: 'relative',
  zIndex: theme.zIndex.appBar + 1,
  borderRadius: 20,
  border: '1px solid #EAEAE3',
  backgroundColor: '#FFFDFA',
  '&:hover': {
    backgroundColor: '#FFFDFA',
  },
  width: '100%',
  //display: 'none',
  paddingLeft: 15,
  boxSizing: 'border-box',
  [theme.breakpoints.up('sm')]: {
    display: 'block',
    width: 'auto',
  }
}));

const SearchIconWrapper = styled('div')(({ theme }) => ({
  padding: theme.spacing(0, 1),
  height: '100%',
  position: 'absolute',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  right: 0,
  top: 0
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: 'inherit',
  position: 'relative',
  width: '100%',
  '& .MuiInputBase-input': {
    //fontSize: theme.typography.fontSize, 
    padding: theme.spacing(1, 1, 1, 0),
    paddingRight: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create('width'),
    width: '100%',
    [theme.breakpoints.up('lg')]: {
      width: '34ch',
    },
  },
  '& input::placeholder': {
    color: 'inherit',
    opacity: 1
  }
}));

const Listbox = styled('ul')(({ theme }) => ({
  width: '99.5%',
  margin: '-20px 0 0 0',
  padding: '30px 10px 10px 10px',
  boxSizing: 'border-box',
  zIndex: theme.zIndex.appBar,
  color: theme.palette.text.primary,
  position: 'absolute',
  listStyle: 'none',
  backgroundColor: theme.palette.background.paper,
  overflow: 'auto',
  maxHeight: 400,
  borderRadius: '0 0 20px 20px',
  border: '1px solid #EAEAE3',
  boxShadow: '0px 3px 6px #00000029',
  '& li': {
    padding: theme.spacing(1),
    borderRadius: 18
  },
  [`& li.${autocompleteClasses.focused}`]: {
    backgroundColor: theme.palette.cream.main,
    cursor: 'pointer',
  },
  '& li:active': {
    backgroundColor: theme.palette.cream.main,
  },
}));

const Search = (props) => {

  const { id } = props;
  const {theme, isMobile} = useLayout();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [keyword, setKeyword] = React.useState(''); 
  const [value, setValue] = React.useState({});

  const { data = [], isFetching } = useAutocompleteQuery(keyword, {
    skip: keyword.length <= 2
  });

  const {
    getRootProps,
    getInputLabelProps,
    getInputProps,
    getListboxProps,
    getOptionProps,
    groupedOptions,
  } = useAutocomplete({
    id: id,
    options: data,
    loading: isFetching,
    getOptionLabel: (option) => option.title || '',
    value: value,
    open: keyword.length > 2,
    inputValue: keyword,
    onInputChange: (event, newInputValue) => {
      setKeyword(newInputValue);
    },
    onChange: (event, newValue) => {
      setValue(newValue);
    },
    filterOptions: (options, state) => {
      return options;
    }
  });

  const parseOptionTitle = (title, keyword) => {
    return title.toLowerCase().split(' ').map((wordTitle) => {
      keyword.toLowerCase().split(' ').forEach((word) => {
        word = word.trim();
        if(Boolean(word) && wordTitle.includes(word)) {
          wordTitle = wordTitle.replace(word, '**' + word + '**');
        }
      })
      return wordTitle;
    }).join(' ');
  }

  React.useEffect(() => {
    if(value && value.slug) {
      navigate(generatePath('/ricette/:slug', {slug: value.slug}));
    }
  }, [value]);

  React.useEffect(() => {
    if(value.slug) {
      setValue({});
    }
    if(keyword) {
      setKeyword('');
    }
  }, [location]);

  return(
    <Box
      position="relative"
    >
      <SearchContainer
        {...getRootProps()}
        sx={{
          color: Boolean(keyword) && theme.palette.text.primary
        }}
      >
        <StyledInputBase
          placeholder={t("navigationBar_search_web")}
          inputProps={{
            ...getInputProps(),
            onKeyPress: (event) => {
              if(event.key === 'Enter' && !isFetching) {
                navigate(generatePath('/ricette?q=:q', {q: keyword}))
              }
            }
          }}
        />
        <SearchIconWrapper>
          <IconButton 
            disabled={!Boolean(keyword)}
            color="inherit"
            onClick={() => navigate(generatePath('/ricette?q=:q', {q: keyword})) }
          >
            <SearchIcon color="inherit" fontSize="small"/>
          </IconButton>
        </SearchIconWrapper>
      </SearchContainer>
      {
        Boolean(groupedOptions.length) && 
        <>
          <Listbox {...getListboxProps()}>
            {groupedOptions.map((option, index) => (
              <li {...getOptionProps({ option, index })} key={option.id}>
                <Typography 
                  component={Markdown} 
                  color="textPrimary" 
                  sx={{
                    '&:first-letter': {
                      textTransform: 'capitalize'
                    }
                  }}
                >
                  { 
                    Boolean(keyword) ? 
                    parseOptionTitle(option.title, keyword) :
                    option.title.toLowerCase() 
                  }                
                </Typography>
              </li>
            ))}
          </Listbox>
          <Backdrop open sx={{zIndex: (theme) => theme.zIndex.appBar - 1}} />
        </>
      }
    </Box>
  );
}

export default Search;